import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Pagination,
  Paper,
  Chip,
  Skeleton,
  Alert,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";

import {
  agencyAllCategoryApi,
  agencyAllSubCategoryApi,
  AgencyListApi,
  ExpertSubCategoryApi,
  serviceCategoryApi,
} from "../Component/API/Api";

// NOTE: two new icon imports below — add these if your project doesn't
// already have them (they ship with @mui/icons-material, no extra install).
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";

import AgencyCardItemNew from "../Component/Homepage Components/Agency/AgencyCardItemNew";
import Seo from "../store/Seo";

const PAGE_SIZE_FALLBACK = 12;
// NOTE: AgencyListApi is used for the unpaged/"all categories" view (see the
// fetch effect below). agencyAllCategoryApi is used once a specific category
// is selected, agencyAllSubCategoryApi once a subcategory is selected, and
// the dedicated search endpoint handles free-text search.

const ACCENT = "#0F766E"; // teal accent used across the filter bar
const ACCENT_SOFT = "rgba(15, 118, 110, 0.08)";
const ACCENT_BORDER = "rgba(15, 118, 110, 0.35)";

// Shared "pill" look for the two Select filters — a soft tint + accent
// border once a value is chosen, so the filter bar visually communicates
// active state at a glance.
const pillSelectSx = (hasValue, disabled) => ({
  borderRadius: 999,
  bgcolor: hasValue ? ACCENT_SOFT : "background.paper",
  opacity: disabled ? 0.55 : 1,
  transition: "border-color 0.15s ease, background-color 0.15s ease",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: hasValue ? ACCENT_BORDER : "divider",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: disabled ? undefined : ACCENT,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
    borderWidth: 1.5,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    gap: 1,
    py: 1.15,
    pl: 2,
    pr: 4,
    fontSize: 14.5,
    fontWeight: hasValue ? 600 : 500,
    color: hasValue ? ACCENT : "text.primary",
    whiteSpace: "nowrap",
  },
  "& .MuiSelect-icon": {
    color: hasValue ? ACCENT : "text.secondary",
  },
});

// Rounded, elevated dropdown panel (instead of the default square Paper) —
// shared by both Select filters.
const selectMenuProps = {
  PaperProps: {
    elevation: 0,
    sx: {
      mt: 1,
      borderRadius: 2.5,
      border: "1px solid",
      borderColor: "divider",
      boxShadow: "0 16px 40px -12px rgba(15, 23, 42, 0.22)",
      maxHeight: 340,
      minWidth: 200,
      py: 0.5,
      "& .MuiMenuItem-root": {
        borderRadius: 1.5,
        mx: 0.75,
        my: 0.25,
        px: 1.5,
        fontSize: 14,
        "&:hover": { bgcolor: ACCENT_SOFT },
        "&.Mui-selected": {
          bgcolor: ACCENT_SOFT,
          fontWeight: 600,
          "&:hover": { bgcolor: ACCENT_SOFT },
        },
      },
    },
  },
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

const AgencyList = () => {
  const { refer } = useParams(); // optional category_id from the URL
  const navigate = useNavigate();

  const [category, setCategory] = useState(""); // name
  const [selectedCategoryId, setSelectedCategoryId] = useState(refer || ""); // id
  const [subCategory, setSubCategory] = useState(""); // name
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(""); // id

  const [categoryListData, setCategoryListData] = useState([]);
  const [subCategoryListData, setSubCategoryListData] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const searchTimerRef = useRef(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);

  const [agencyData, setAgencyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Helper: Create FormData
  const createFormData = (pref, page = null, action = "fetch") => {
    const formData = new FormData();
    formData.append("pref", pref ?? 1);
    if (page) formData.append("page", page);
    formData.append("action", action);
    return formData;
  };

  // --- Fetch category list on first load ---
  useEffect(() => {
    fetch(serviceCategoryApi)
      .then((res) => res.json())
      .then((data) => setCategoryListData(Array.isArray(data) ? data : []))
      .catch((error) => console.log("Category API Error:", error));
  }, []);

  // --- Resolve the category name once we know both the id (from the URL) and the list ---
  useEffect(() => {
    if (!refer || categoryListData.length === 0) return;
    const found = categoryListData.find((item) => item.category_id === refer);
    if (found) setCategory(found.category_name);
  }, [refer, categoryListData]);

  // --- Fetch subcategory options whenever the selected category changes ---
  useEffect(() => {
    if (!selectedCategoryId) {
      setSubCategoryListData([]);
      return;
    }

    const formData = createFormData(selectedCategoryId);
    fetch(ExpertSubCategoryApi, { method: "POST", body: formData })
      .then((res) => res.json())
      .then((data) => setSubCategoryListData(Array.isArray(data) ? data : []))
      .catch((error) => console.log("Subcategory API Error:", error));
  }, [selectedCategoryId]);

  // --- Debounce the search box so we don't fire a request on every keystroke ---
  useEffect(() => {
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchTerm]);

  // --- Single source of truth for fetching agency data ---
  // Priority: search > subcategory > category > all categories (AgencyListApi)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError("");

      try {
        let result;

        if (debouncedSearchTerm) {
          const res = await fetch(
            `https://aps.songjukti.com/profile/login/api_freelancer/new_24/agency_all_search.php?s_query=${encodeURIComponent(
              debouncedSearchTerm
            )}&page=${page}`
          );
          result = await res.json();
        } else if (selectedSubCategoryId) {
          const formData = createFormData(selectedSubCategoryId, page);
          const res = await fetch(agencyAllSubCategoryApi, {
            method: "POST",
            body: formData,
          });
          result = await res.json();
        } else if (selectedCategoryId) {
          const formData = createFormData(selectedCategoryId, page);
          const res = await fetch(agencyAllCategoryApi, {
            method: "POST",
            body: formData,
          });
          result = await res.json();
        } else {
          // "All categories" selected (no category/subcategory/search filter)
          const formData = createFormData(1, page);
          const res = await fetch(AgencyListApi, {
            method: "POST",
            body: formData,
          });
          result = await res.json();
        }

        if (cancelled) return;

        const data = result?.data ?? result;
        setAgencyData(Array.isArray(data) ? data : []);

        if (result?.total_pages) {
          setTotalPages(result.total_pages);
        } else if (result?.total && result?.per_page) {
          setTotalPages(Math.ceil(result.total / result.per_page));
        } else {
          setTotalPages(1);
        }
        setTotalResults(
          typeof result?.total === "number" ? result.total : null
        );
      } catch (err) {
        if (!cancelled) {
          console.log("Agency Fetch Error:", err);
          setError("We couldn't load agencies right now. Please try again.");
          setAgencyData([]);
          setTotalPages(1);
          setTotalResults(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm, selectedSubCategoryId, selectedCategoryId, page]);

  // --- Handlers ---
  const handleCategoryChange = (e) => {
    const val = e.target.value;
    setCategory(val);
    setSubCategory("");
    setSelectedSubCategoryId("");
    setPage(1);

    if (!val) {
      setSelectedCategoryId("");
      return;
    }
    const found = categoryListData.find((item) => item.category_name === val);
    setSelectedCategoryId(found?.category_id || "");
  };

  const handleSubCategoryChange = (e) => {
    const val = e.target.value;
    setSubCategory(val);
    setPage(1);

    if (!val) {
      setSelectedSubCategoryId("");
      return;
    }
    const found = subCategoryListData.find((item) => item.sub_name === val);
    setSelectedSubCategoryId(found?.sub_id || "");
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSearchNow = () => {
    clearTimeout(searchTimerRef.current);
    setDebouncedSearchTerm(searchTerm.trim());
    setPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearchNow();
  };

  const handleClearSearch = () => {
    clearTimeout(searchTimerRef.current);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearFilters = () => {
    setCategory("");
    setSelectedCategoryId("");
    setSubCategory("");
    setSelectedSubCategoryId("");
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(1);
  };

  const hasActiveFilters = Boolean(category || subCategory || debouncedSearchTerm);

  return (
    <Box sx={{ bgcolor: "#F7F8FA", minHeight: "100vh" }}>
      <Container sx={{ mt: 0, width: "100%", pb: 6, px: { xs: 2, sm: 3 } }}>
        <Seo
          title="Find Professional Freelance  in Bangladesh"
          description="Browse top-rated freelancers and agencies in design, development, writing, photography, and marketing — all in one place on Songjukti."
          keywords="freelancer services Bangladesh, hire designers, hire developers, Songjukti freelancers, creative agencies"
          url="https://www.songjukti.com/expertlist"
        />

        {/* Filter bar */}
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 3, md: 5 },
            p: { xs: 2, sm: 2.5, md: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            boxShadow:
              "0 1px 2px rgba(16,24,40,0.04), 0 12px 28px -16px rgba(16,24,40,0.10)",
          }}
        >
          {/* Search — full-width pill with a leading icon and inline actions */}
          <OutlinedInput
            fullWidth
            size="small"
            placeholder="Search agencies by name or skill…"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            startAdornment={
              <InputAdornment position="start">
                <Search sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                {searchTerm && (
                  <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                    <Close fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={handleSearchNow}
                  aria-label="Search"
                  sx={{
                    ml: 0.5,
                    bgcolor: ACCENT,
                    color: "#fff",
                    "&:hover": { bgcolor: "#0C5D57" },
                  }}
                >
                  <Search fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
            sx={{
              borderRadius: 999,
              bgcolor: "background.paper",
              fontSize: 15,
              pl: 1,
              pr: 0.75,
              py: 0.25,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: ACCENT,
                borderWidth: 1.5,
              },
            }}
          />

          {/* Category / subcategory — compact pill selects, wrap freely on small screens */}
          <Stack
            direction="row"
            flexWrap="wrap"
            spacing={1.25}
            sx={{ mt: 2, rowGap: 1.25 }}
          >
            <FormControl
              size="small"
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 190 } }}
            >
              <Select
                value={category}
                onChange={handleCategoryChange}
                displayEmpty
                MenuProps={selectMenuProps}
                sx={pillSelectSx(Boolean(category))}
                renderValue={(val) => (
                  <>
                    <CategoryRoundedIcon
                      sx={{ fontSize: 18, opacity: val ? 1 : 0.55 }}
                    />
                    {val || "All categories"}
                  </>
                )}
              >
                <MenuItem value="">
                  <em>All categories</em>
                </MenuItem>
                {categoryListData.map((item) => (
                  <MenuItem value={item.category_name} key={item.category_id}>
                    {item.category_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              disabled={!category}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 190 } }}
            >
              <Select
                value={subCategory}
                onChange={handleSubCategoryChange}
                displayEmpty
                disabled={!category}
                MenuProps={selectMenuProps}
                sx={pillSelectSx(Boolean(subCategory), !category)}
                renderValue={(val) => (
                  <>
                    <LayersRoundedIcon
                      sx={{ fontSize: 18, opacity: val ? 1 : 0.55 }}
                    />
                    {val || "All sub categories"}
                  </>
                )}
              >
                <MenuItem value="">
                  <em>All sub categories</em>
                </MenuItem>
                {subCategoryListData.map((item) => (
                  <MenuItem value={item.sub_name} key={item.sub_id}>
                    {item.sub_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Chip
                label="Clear all"
                size="small"
                variant="outlined"
                onClick={clearFilters}
                sx={{
                  borderRadius: 999,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  fontWeight: 500,
                }}
              />
            )}
          </Stack>

          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {debouncedSearchTerm && (
                <Chip
                  label={`"${debouncedSearchTerm}"`}
                  size="small"
                  onDelete={handleClearSearch}
                  deleteIcon={<Close fontSize="small" />}
                  sx={{ borderRadius: 999, bgcolor: ACCENT_SOFT, color: ACCENT, fontWeight: 500 }}
                />
              )}
              {category && (
                <Chip
                  label={category}
                  size="small"
                  onDelete={() => {
                    setCategory("");
                    setSelectedCategoryId("");
                    setSubCategory("");
                    setSelectedSubCategoryId("");
                    setPage(1);
                  }}
                  deleteIcon={<Close fontSize="small" />}
                  sx={{ borderRadius: 999, bgcolor: ACCENT_SOFT, color: ACCENT, fontWeight: 500 }}
                />
              )}
              {subCategory && (
                <Chip
                  label={subCategory}
                  size="small"
                  onDelete={() => {
                    setSubCategory("");
                    setSelectedSubCategoryId("");
                    setPage(1);
                  }}
                  deleteIcon={<Close fontSize="small" />}
                  sx={{ borderRadius: 999, bgcolor: ACCENT_SOFT, color: ACCENT, fontWeight: 500 }}
                />
              )}
            </Stack>
          )}
        </Paper>

        {/* Results summary */}
        {!error && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={0.5}
            sx={{ mt: 3, mb: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              {loading
                ? "Loading agencies…"
                : totalResults !== null
                ? `${totalResults} agenc${totalResults === 1 ? "y" : "ies"} found`
                : `${agencyData.length} agenc${agencyData.length === 1 ? "y" : "ies"} on this page`}
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Agency Grid — 1 col on phones, 2 on small tablets, 3 on desktop, 4 on wide screens */}
        <Grid container spacing={2} mt={loading || error ? 0 : 1} mb={4}>
          {loading ? (
            Array.from({ length: PAGE_SIZE_FALLBACK }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${i}`}>
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
                <Skeleton variant="text" sx={{ mt: 1 }} />
                <Skeleton variant="text" width="60%" />
              </Grid>
            ))
          ) : agencyData.length > 0 ? (
            agencyData.map((element) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={element.agency_info?.c_refer}>
                <AgencyCardItemNew agencyData={element} />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  py: 6,
                  px: 2,
                  textAlign: "center",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 3,
                }}
              >
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  No agencies found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try a different search term, category, or clear your filters.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Stack alignItems="center" sx={{ mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              color="primary"
              siblingCount={0}
              boundaryCount={1}
              showFirstButton
              showLastButton
              size="medium"
              sx={{
                mb: 4,
                "& .MuiPaginationItem-root": { borderRadius: 999 },
              }}
            />
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default AgencyList;